import gsap from 'gsap';

function initVideoBackground() {
    const video = document.getElementById('bgVideo');
    const sequence = document.getElementById('landingScrollSequence');
    const progressBar = document.querySelector('.video-scroll-progress__bar');
    const scrollCue = document.querySelector('.video-scroll-cue');
    const cueDivider = document.querySelector('.video-scroll-cue__divider');

    if (!video || !sequence) {
        return;
    }

    const media = gsap.matchMedia();

    media.add({
        all: 'all',
        reduceMotion: '(prefers-reduced-motion: reduce)',
    }, (context) => {
        const { reduceMotion } = context.conditions;
        const hintTimeline = reduceMotion
            ? null
            : gsap.timeline({
                defaults: { ease: 'power3.out' },
            })
                .fromTo(scrollCue, {
                    autoAlpha: 0,
                    y: 12,
                }, {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.6,
                    delay: 0.65,
                })
                .fromTo(cueDivider, {
                    scaleX: 0.2,
                    transformOrigin: 'center center',
                }, {
                    scaleX: 1,
                    duration: 0.7,
                }, '<0.12');

        video.pause();

        const stageContentTargets = gsap.utils.toArray('.landing-stage .content img, .landing-stage .circular-button, .sticky-left .btn');
        let contentIdleTimeout;
        let contentHidden = false;

        const revealStageContent = () => {
            contentHidden = false;
            gsap.to(stageContentTargets, {
                autoAlpha: 1,
                y: 0,
                duration: reduceMotion ? 0 : 0.4,
                ease: 'power3.out',
                overwrite: 'auto',
            });
        };

        const handleScrubActivity = () => {
            clearTimeout(contentIdleTimeout);

            if (!contentHidden) {
                contentHidden = true;
                gsap.to(stageContentTargets, {
                    autoAlpha: 0,
                    y: reduceMotion ? 0 : -12,
                    duration: reduceMotion ? 0 : 0.25,
                    ease: 'power2.out',
                    overwrite: 'auto',
                });
            }

            contentIdleTimeout = setTimeout(revealStageContent, 220);
        };

        const createScrubAnimation = () => {
            const playableDuration = Math.max(video.duration - 0.05, 0.05);
            const wrapTime = gsap.utils.wrap(0, playableDuration);
            const clampDelta = gsap.utils.clamp(-140, 140);
            const frameStep = 1 / 30;
            const playhead = { time: 0 };
            let targetTime = 0;
            let playheadTween;
            let activeDirection;
            let lastTouchY;
            let lastSeekTime;

            video.currentTime = 0;
            lastSeekTime = 0;
            gsap.set(progressBar, {
                scaleX: 0,
                transformOrigin: 'left center',
            });

            const renderFrame = () => {
                const time = wrapTime(playhead.time);
                const quantizedTime = Math.round(time / frameStep) * frameStep;

                if (quantizedTime !== lastSeekTime) {
                    lastSeekTime = quantizedTime;
                    video.currentTime = quantizedTime;
                }

                gsap.set(progressBar, { scaleX: time / playableDuration });
            };

            const updateDirectionCue = (direction) => {
                if (reduceMotion || direction === activeDirection) {
                    return;
                }

                activeDirection = direction;
                gsap.to(scrollCue.querySelectorAll('[data-scroll-direction]'), {
                    autoAlpha: 0.48,
                    x: 0,
                    duration: 0.18,
                    overwrite: 'auto',
                });
                gsap.fromTo(
                    scrollCue.querySelectorAll(`[data-scroll-direction="${direction}"]`),
                    { x: direction === 'forward' ? -4 : 4 },
                    {
                        autoAlpha: 1,
                        x: 0,
                        duration: 0.32,
                        ease: 'power3.out',
                        overwrite: 'auto',
                    },
                );
            };

            const movePlayhead = (deltaY, sensitivity = 0.003) => {
                targetTime += clampDelta(deltaY) * sensitivity;
                updateDirectionCue(deltaY >= 0 ? 'forward' : 'reverse');
                handleScrubActivity();

                playheadTween = gsap.to(playhead, {
                    time: targetTime,
                    duration: reduceMotion ? 0 : 0.18,
                    ease: 'power1.out',
                    overwrite: true,
                    onUpdate: renderFrame,
                });
            };

            const handleWheel = (event) => {
                if (Math.abs(event.deltaY) < 1) {
                    return;
                }

                event.preventDefault();
                const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE
                    ? 16
                    : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
                        ? window.innerHeight
                        : 1;

                movePlayhead(event.deltaY * unit);
            };
            const handleTouchStart = (event) => {
                lastTouchY = event.touches[0]?.clientY;
            };
            const handleTouchMove = (event) => {
                const nextTouchY = event.touches[0]?.clientY;

                if (lastTouchY === undefined || nextTouchY === undefined) {
                    return;
                }

                const deltaY = lastTouchY - nextTouchY;

                if (Math.abs(deltaY) < 1) {
                    return;
                }

                event.preventDefault();
                lastTouchY = nextTouchY;
                movePlayhead(deltaY, 0.008);
            };
            const handleTouchEnd = () => {
                lastTouchY = undefined;
            };

            sequence.addEventListener('wheel', handleWheel, { passive: false });
            sequence.addEventListener('touchstart', handleTouchStart, { passive: true });
            sequence.addEventListener('touchmove', handleTouchMove, { passive: false });
            sequence.addEventListener('touchend', handleTouchEnd, { passive: true });
            sequence.addEventListener('touchcancel', handleTouchEnd, { passive: true });

            return () => {
                sequence.removeEventListener('wheel', handleWheel);
                sequence.removeEventListener('touchstart', handleTouchStart);
                sequence.removeEventListener('touchmove', handleTouchMove);
                sequence.removeEventListener('touchend', handleTouchEnd);
                sequence.removeEventListener('touchcancel', handleTouchEnd);
                playheadTween?.kill();
            };
        };

        let destroyScrub;
        const handleLoadedMetadata = () => {
            destroyScrub?.();
            destroyScrub = createScrubAnimation();
        };

        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            handleLoadedMetadata();
        } else {
            video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
        }

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            destroyScrub?.();
            hintTimeline?.kill();
            clearTimeout(contentIdleTimeout);
            gsap.set(stageContentTargets, { clearProps: 'transform,opacity,visibility' });
        };
    });
}

document.addEventListener('DOMContentLoaded', initVideoBackground);
